import { useState, useEffect } from "react";
import { getLyrics, getComments, postComment, postVote, getUserVotes, saveUserVote } from "../api";

const GCS = "https://storage.googleapis.com/myaimusic";
const GENRE_COLS = {
  pop:        "#7adb2a",
  electronic: "#9b27af",
  rock:       "#ff7a00",
};

function parseLyrics(text) {
  if (!text) return [];
  const sections = [];
  let current = null;
  text.split("\n").forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const isLabel =
      /^(verse|chorus|pre-chorus|bridge|outro|intro|build|breakdown|drop)/i.test(trimmed)
      && trimmed.length < 40;
    if (isLabel) {
      if (current) sections.push(current);
      current = { label: trimmed, lines: [] };
    } else {
      if (!current) current = { label: "", lines: [] };
      current.lines.push(trimmed);
    }
  });
  if (current) sections.push(current);
  return sections;
}

export default function SongRow({ song, isActive, onSelect, onAddToQueue, userId }) {
  const [expanded, setExpanded]   = useState(false);
  const [lyrics, setLyrics]       = useState(null);
  const [comments, setComments]   = useState([]);
  const [author, setAuthor]       = useState("");
  const [text, setText]           = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [myVote, setMyVote]       = useState(null);
  const [votes, setVotes]         = useState({
    up:   song.votes_up   || 0,
    down: song.votes_down || 0,
  });

  const col      = GENRE_COLS[song.genre?.toLowerCase()] || "#ff2d9b";
  const coverUrl = song.cover_file
    ? `${GCS}/cover_art/${song.cover_file}`
    : null;

  // Load this user's existing vote when userId is available
  useEffect(() => {
    if (!userId) return;
    getUserVotes(userId).then(userVotes => {
      if (userVotes[song.id]) {
        setMyVote(userVotes[song.id]);
      }
    }).catch(console.warn);
  }, [userId, song.id]);

  async function handleExpand() {
    if (!expanded) {
      const [l, c] = await Promise.all([
        getLyrics(song.id),
        getComments(song.id),
      ]);
      setLyrics(l.lyrics);
      setComments(c);
    }
    setExpanded(!expanded);
  }

  async function handleComment() {
    if (!text.trim()) return;
    await postComment(song.id, author || "Anonymous", text);
    setText("");
    setAuthor("");
    setSubmitted(true);
  }

  async function handleVote(dir, e) {
    e.stopPropagation();
    const newVote = myVote === dir ? null : dir;

    // Optimistic UI update
    setVotes(v => ({
      up:   v.up   + (dir === "up"   ? (newVote ? 1 : -1) : (myVote === "up"   ? -1 : 0)),
      down: v.down + (dir === "down" ? (newVote ? 1 : -1) : (myVote === "down" ? -1 : 0)),
    }));
    setMyVote(newVote);

    // Persist vote count to Firestore
    if (newVote) {
      await postVote(song.id, newVote).catch(console.warn);
    }

    // Persist user's choice
    if (userId) {
      await saveUserVote(userId, song.id, newVote || "none").catch(console.warn);
    }
  }

  function handleAddToQueue(e) {
    e.stopPropagation();
    onAddToQueue(song);
  }

  const lyricSections = parseLyrics(lyrics);

  return (
    <>
      <div
        className={`track ${isActive ? "active" : ""}`}
        onClick={() => onSelect(song)}
      >
        {/* COVER */}
        <div className="cover">
          {coverUrl && <img src={coverUrl} alt={song.title} />}
          <div className="cov-over">
            <div
              className="play-circ"
              onClick={e => { e.stopPropagation(); onSelect(song); }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="#fff">
                <path d="M3 1.5l6 3.5-6 3.5z" />
              </svg>
            </div>
          </div>
        </div>

        {/* INFO */}
        <div className="info" onClick={e => { e.stopPropagation(); handleExpand(); }}>
          <div className="t-name-row">
            <div className="t-name">{song.title}</div>
            <div className="track-viz">
              {[0, 1, 2, 3].map((_, i) => (
                <div key={i} className="viz-bar" style={{
                  background:        col,
                  height:            "100%",
                  animationDelay:    `${i * 0.12}s`,
                  animationDuration: `${0.4 + i * 0.1}s`,
                }} />
              ))}
            </div>
          </div>

          <div className="t-row">
            <span className="t-genre" style={{
              color:       col,
              borderColor: col + "50",
              background:  col + "12",
            }}>
              {song.genre || "—"}
            </span>
            <span className="t-detail">
              {song.model} · {song.bpm} BPM · {song.key}
            </span>
          </div>

          <div className="t-plays">
            {(song.play_count || 0).toLocaleString()} plays
            {song.generated_at ? ` · ${song.generated_at}` : ""}
            {votes.up > 0 && (
              <span style={{ marginLeft: 8, color: "#7adb2a", fontWeight: 700 }}>
                ▲ {votes.up}
              </span>
            )}
            {votes.down > 0 && (
              <span style={{ marginLeft: 6, color: "#ff2d9b", fontWeight: 700 }}>
                ▼ {votes.down}
              </span>
            )}
          </div>
        </div>

        {/* ACTIONS */}
        <div className="track-actions">
          <div className="t-dur">{song.duration || "—"}</div>
          <div className="track-btns">
            <button
              className={`t-btn ${myVote === "up" ? "voted" : ""}`}
              onClick={e => handleVote("up", e)}
              title="Upvote"
            >
              ▲ {votes.up}
            </button>
            <button
              className={`t-btn ${myVote === "down" ? "voted" : ""}`}
              onClick={e => handleVote("down", e)}
              title="Downvote"
            >
              ▼ {votes.down}
            </button>
            <button
              className="t-btn queue-add"
              onClick={handleAddToQueue}
              title="Add to queue"
            >
              + Queue
            </button>
          </div>
        </div>
      </div>

      {/* EXPANDED PANEL */}
      {expanded && (
        <div className="expand">
          {/* TRACK INFO */}
          <div>
            <div className="el">Track Info</div>
            <div className="mrow">
              <span className="mk">Model</span>
              <span className="mv hi">{song.model || "—"}</span>
            </div>
            <div className="mrow">
              <span className="mk">Tempo</span>
              <span className="mv">{song.bpm ? `${song.bpm} BPM` : "—"}</span>
            </div>
            <div className="mrow">
              <span className="mk">Key</span>
              <span className="mv">{song.key || "—"}</span>
            </div>
            <div className="mrow">
              <span className="mk">Duration</span>
              <span className="mv">{song.duration || "—"}</span>
            </div>
            <div className="mrow">
              <span className="mk">Plays</span>
              <span className="mv">{(song.play_count || 0).toLocaleString()}</span>
            </div>
            <div className="mrow">
              <span className="mk">Score</span>
              <span className="mv hi">▲ {votes.up} · ▼ {votes.down}</span>
            </div>
          </div>

          {/* LYRICS */}
          <div>
            <div className="el">Lyrics</div>
            <div className="lyrics">
              {lyricSections.length > 0
                ? lyricSections.map((sec, i) => (
                    <div key={i} className="lyric-section">
                      {sec.label && (
                        <div className="lyric-label">{sec.label}</div>
                      )}
                      {sec.lines.map((line, j) => (
                        <div key={j} className="lyric-line">{line}</div>
                      ))}
                    </div>
                  ))
                : <div className="lyric-line">{lyrics ?? "loading..."}</div>
              }
            </div>
          </div>

          {/* COMMENTS */}
          <div className="expand-full cmt-area">
            <div className="el">Leave a note</div>
            {submitted ? (
              <p className="cmt-thanks">
                Thanks — your note is pending moderation.
              </p>
            ) : (
              <div className="cmt-row">
                <input
                  className="cmt-in"
                  placeholder="Your name (optional)"
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                />
                <input
                  className="cmt-in"
                  placeholder="What did this sound like to you?"
                  value={text}
                  onChange={e => setText(e.target.value)}
                />
                <button className="cmt-btn" onClick={handleComment}>
                  Post
                </button>
              </div>
            )}
            <div className="cmt-list">
              {comments.length === 0 && lyrics !== null && (
                <div className="cmt-item" style={{ color: "#ccc", fontStyle: "italic" }}>
                  No comments yet — be the first!
                </div>
              )}
              {comments.map(c => (
                <div key={c.id} className="cmt-item">
                  {c.author && (
                    <span style={{ fontWeight: 600, marginRight: 6 }}>
                      {c.author}
                    </span>
                  )}
                  {c.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}